
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { useToast } from '@/components/ui/use-toast';
import { getFlights } from '@/services/flightService';
import { Search, Edit } from 'lucide-react';
import { Flight } from '@/types/flight';
import { useFlightUpdate } from '@/hooks/useFlightUpdate';
import { FlightSelector } from './flight-update/FlightSelector';
import { SelectedFlightInfo } from './flight-update/SelectedFlightInfo';
import { StatusUpdateForm } from './flight-update/StatusUpdateForm';
import { flightUpdateSchema, FlightUpdateFormValues } from './flight-update/schemas';
import { FlightSearchSection } from './flight-search/FlightSearchSection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export function FlightUpdateForm() {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('search');
  const { toast } = useToast();
  const { updating, updateFlightData } = useFlightUpdate();

  const form = useForm<FlightUpdateFormValues>({
    resolver: zodResolver(flightUpdateSchema),
    defaultValues: {
      status: "On Time",
    },
  });

  useEffect(() => {
    const fetchFlights = async () => {
      try {
        // Filter to show only today's flights
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const flightsData = await getFlights({ date: today });
        setFlights(flightsData);
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "Error loading flights",
          description: error.message || "Failed to load flights.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFlights();
  }, [toast]);

  const handleSubmit = async (values: FlightUpdateFormValues) => {
    const success = await updateFlightData(values.flight_id, {
      status: values.status,
      scheduled_departure: values.scheduled_departure,
      scheduled_arrival: values.scheduled_arrival,
      status_reason: values.status_reason,
    });
    
    if (success) {
      form.reset();
      // Refresh flights list with today's filter
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const flightsData = await getFlights({ date: today });
      setFlights(flightsData);
      toast({
        title: "Flight updated successfully",
        description: "The flight information has been updated and the list has been refreshed.",
      });
    }
  };

  const handleSelectFlightFromSearch = (flight: Flight) => {
    // Pre-populate the form with selected flight data
    form.setValue('flight_id', flight.id);
    form.setValue('status', flight.status);
    
    // Convert ISO strings to datetime-local format for the form inputs
    if (flight.scheduled_departure) {
      const departureDate = new Date(flight.scheduled_departure);
      form.setValue('scheduled_departure', departureDate.toISOString().slice(0, 16));
    }
    
    if (flight.scheduled_arrival) {
      const arrivalDate = new Date(flight.scheduled_arrival);
      form.setValue('scheduled_arrival', arrivalDate.toISOString().slice(0, 16));
    }
    
    if (flight.status_reason) {
      form.setValue('status_reason', flight.status_reason);
    }
    
    // Switch to update tab
    setActiveTab('update');
    
    toast({
      title: "Flight selected",
      description: `Flight ${flight.flight_number} has been selected for update.`,
    });
  };

  const selectedFlight = flights.find(f => f.id === form.watch('flight_id'));

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
          Update Flight Information
        </CardTitle>
        <CardDescription>
          Search for flights and update their status and schedule information
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="search" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search Flights
            </TabsTrigger>
            <TabsTrigger value="update" className="flex items-center gap-2">
              <Edit className="h-4 w-4" />
              Update Flight
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="space-y-6">
            <FlightSearchSection onSelectFlight={handleSelectFlightFromSearch} />
          </TabsContent>

          <TabsContent value="update" className="space-y-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FlightSelector 
                  control={form.control} 
                  flights={flights} 
                  loading={loading}
                />

                {selectedFlight && (
                  <SelectedFlightInfo flight={selectedFlight} />
                )}

                <StatusUpdateForm control={form.control} />

                <Button type="submit" disabled={updating || !form.watch('flight_id')} className="w-full">
                  {updating ? "Updating..." : "Update Flight"}
                </Button>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
